import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { OrderedSet as ImmutableOrderedSet } from 'immutable';
import { getMessageResultsFromStagedThread } from '../quick-reply/operators/getMessageResultsFromStagedThread';
import getQuickReplyOptionsFromStagedThreadMessages from '../quick-reply/operators/getQuickReplyOptionsFromStagedThreadMessages';
import { showQuickReplyOutsideChat } from '../utils/showQuickRepliesOutsideChat';
import { selectQuickReplyOptionOutsideChat } from '../actions/selectQuickReplyOptionOutsideChat';
import { getColoring } from '../selectors/widgetDataSelectors/getColoring';
import { getIsUngatedForUpdatedChatLauncherExperience } from '../widget-data/selectors/widgetDataSelectors';
import { useConsentPromptDataV2 } from './useConsentPromptDataV2';
const EMPTY_QUICK_REPLIES = ImmutableOrderedSet();
export const useWelcomeMessageConfig = () => {
  const dispatch = useDispatch();
  const {
    shouldHideQuickReplies,
    showConsentButton,
    consentMessage,
    consentRequired,
    spamProtectionEnabled,
    handleConsentAccept
  } = useConsentPromptDataV2();
  const isUngatedForUpdatedLauncher = useSelector(getIsUngatedForUpdatedChatLauncherExperience);
  const messageResults = useSelector(getMessageResultsFromStagedThread);
  const quickReplyOptions = getQuickReplyOptionsFromStagedThreadMessages(messageResults);
  const displayBotQuickRepliesOutsideChat = useSelector(showQuickReplyOutsideChat);
  const widgetColoring = useSelector(getColoring);
  const handleQuickReplyClick = useCallback((text, quickReply) => {
    dispatch(selectQuickReplyOptionOutsideChat(text, quickReply));
  }, [dispatch]);
  const hasQuickReplies = displayBotQuickRepliesOutsideChat && quickReplyOptions.size > 0;
  const showQuickRepliesSection = isUngatedForUpdatedLauncher && hasQuickReplies;
  const shouldShowConsentPrompt = hasQuickReplies && consentRequired;
  const showFooterContent = shouldShowConsentPrompt || spamProtectionEnabled;
  const quickRepliesConfig = useMemo(() => {
    if (!showQuickRepliesSection) {
      return undefined;
    }
    return {
      quickReplyOptions: shouldHideQuickReplies ? EMPTY_QUICK_REPLIES : quickReplyOptions,
      onQuickReplyClick: handleQuickReplyClick,
      accentColor: widgetColoring === null || widgetColoring === void 0 ? void 0 : widgetColoring.accentColor,
      hasQuickReplies: true
    };
  }, [showQuickRepliesSection, shouldHideQuickReplies, quickReplyOptions, handleQuickReplyClick, widgetColoring]);
  const consentConfig = useMemo(() => {
    if (!isUngatedForUpdatedLauncher || !showFooterContent) {
      return undefined;
    }
    return {
      consentMessage: shouldShowConsentPrompt ? consentMessage : '',
      consentRequired: shouldShowConsentPrompt,
      showConsentButton: shouldShowConsentPrompt && showConsentButton,
      onConsentAccept: handleConsentAccept,
      spamProtectionEnabled,
      accentColor: widgetColoring.accentColor
    };
  }, [isUngatedForUpdatedLauncher, showFooterContent, consentMessage, shouldShowConsentPrompt, showConsentButton, handleConsentAccept, spamProtectionEnabled, widgetColoring]);
  return {
    showQuickReplies: showQuickRepliesSection,
    quickRepliesConfig,
    consentConfig
  };
};